import React from 'react';
import type { SxProps, Theme } from '@mui/material/styles';
import {
  Box,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { formatGameSourceLabel } from '../../common/gameSource';
import { formatTimeControlLabel } from '../../common/formatTimeControl';
import { formatUtcDateLabel } from '../../common/formatUtcDate';
import type { ReplayGameSummary } from '../../replay/replayApi';
import EcoOpeningLabel from '../../common/EcoOpeningLabel';
import GameViewedIcon from '../../replay/GameViewedIcon';
import { useGameViewedStatus } from '../../replay/useGameViewedStatus';
import {
  compactTableSx,
  gameSummaryPreviewBoardSx,
  replayGameRowSx,
  seenColumnCellSx,
  wrappedGameRowLine1Sx,
  wrappedGameRowLine2Sx,
  wrappedGameTableSx,
} from '../../common/tableMuiStyles';
import { useWrappedGameRows } from '../../common/useWrappedGameRows';
import { resolveGamePlayerDisplayName } from './playerName';
import GameHoverPreview from '../../replay/GameHoverPreview';
import ReplayGamePreviewBoard from '../../replay/ReplayGamePreviewBoard';
import type { MoveQualityCounts } from 'endchess-contracts';
import GameAnalysisStatusBlock from './GameAnalysisStatusBlock';

type GameAnalysisStatus = 'pending' | 'ready' | 'none' | 'scheduled';

type GameSummaryRow = ReplayGameSummary & {
  analysisStatus?: GameAnalysisStatus;
  moveQualityCounts?: MoveQualityCounts;
};

type GameSummaryTableProps = {
  games: GameSummaryRow[];
  emptyMessage: string;
  onGameSelect: (game: ReplayGameSummary) => void;
  showSource?: boolean;
  showAnalysisStatus?: boolean;
  onAnalyzeGame?: (gameId: string) => void;
  onCancelScheduledAnalysis?: (gameId: string) => void;
  analyzingGameId?: string | null;
  manageMode?: boolean;
  onDeleteGame?: (gameId: string) => void;
  deletingGameId?: string | null;
  canonicalPlayerName?: string;
  /** Abbreviate player names in table cells. Off for player browse drilldown. */
  abbreviatePlayerNames?: boolean;
  wrappedRows?: boolean;
  showReplayAccess?: boolean;
  showPreviewBoardOnFirstRow?: boolean;
  tableSx?: SxProps<Theme>;
};

const columnCount = (showSource: boolean, showReplayAccess: boolean) =>
  (showSource ? 10 : 9) + (showReplayAccess ? 1 : 0);

const GameAnalysisActions = ({
  game,
  analyzingGameId,
  onAnalyzeGame,
  onCancelScheduledAnalysis,
}: {
  game: GameSummaryRow;
  analyzingGameId?: string | null;
  onAnalyzeGame?: (gameId: string) => void;
  onCancelScheduledAnalysis?: (gameId: string) => void;
}) => {
  const status = game.analysisStatus;
  const queueing = analyzingGameId === game.gameId;

  if (status === 'none' && onAnalyzeGame) {
    return (
      <Button
        size="small"
        variant="outlined"
        disabled={queueing}
        onClick={(event) => {
          event.stopPropagation();
          onAnalyzeGame(game.gameId);
        }}
      >
        {queueing ? 'Queueing…' : 'Analyze'}
      </Button>
    );
  }

  if (status === 'pending' && onAnalyzeGame) {
    return (
      <Button
        size="small"
        variant="text"
        disabled={queueing}
        onClick={(event) => {
          event.stopPropagation();
          onAnalyzeGame(game.gameId);
        }}
      >
        {queueing ? 'Retrying…' : 'Retry'}
      </Button>
    );
  }

  if (status === 'scheduled' && onCancelScheduledAnalysis) {
    return (
      <Button
        size="small"
        variant="text"
        onClick={(event) => {
          event.stopPropagation();
          onCancelScheduledAnalysis(game.gameId);
        }}
      >
        Cancel
      </Button>
    );
  }

  return null;
};

const GameManageDeleteAction = ({
  gameId,
  manageMode,
  deletingGameId,
  onDeleteGame,
}: {
  gameId: string;
  manageMode?: boolean;
  deletingGameId?: string | null;
  onDeleteGame?: (gameId: string) => void;
}) => {
  if (!manageMode || !onDeleteGame) {
    return null;
  }

  const deleting = deletingGameId === gameId;

  return (
    <Button
      size="small"
      color="error"
      variant="outlined"
      disabled={deleting}
      onClick={(event) => {
        event.stopPropagation();
        onDeleteGame(gameId);
      }}
    >
      {deleting ? 'Deleting…' : 'Delete'}
    </Button>
  );
};

const gameAnalysisTrailingGroupSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  flexWrap: 'wrap',
  ml: 'auto',
  flexShrink: 0,
  minWidth: 'auto',
} as const;

const GameAnalysisTrailingGroup = ({
  game,
  showAnalysisStatus,
  analyzingGameId,
  onAnalyzeGame,
  onCancelScheduledAnalysis,
  manageMode,
  onDeleteGame,
  deletingGameId,
}: {
  game: GameSummaryRow;
  showAnalysisStatus?: boolean;
  analyzingGameId?: string | null;
  onAnalyzeGame?: (gameId: string) => void;
  onCancelScheduledAnalysis?: (gameId: string) => void;
  manageMode?: boolean;
  onDeleteGame?: (gameId: string) => void;
  deletingGameId?: string | null;
}) => (
  <Box component="span" sx={gameAnalysisTrailingGroupSx}>
    <GameAnalysisActions
      game={game}
      analyzingGameId={analyzingGameId}
      onAnalyzeGame={onAnalyzeGame}
      onCancelScheduledAnalysis={onCancelScheduledAnalysis}
    />
    {showAnalysisStatus ? (
      <GameAnalysisStatusBlock
        analysisStatus={game.analysisStatus}
        moveQualityCounts={game.moveQualityCounts}
      />
    ) : null}
    <GameManageDeleteAction
      gameId={game.gameId}
      manageMode={manageMode}
      deletingGameId={deletingGameId}
      onDeleteGame={onDeleteGame}
    />
  </Box>
);

const renderPlayerName = (
  name: string,
  canonicalPlayerName: string | undefined,
  abbreviatePlayerNames: boolean,
) => {
  const displayName = resolveGamePlayerDisplayName(
    name,
    canonicalPlayerName,
    abbreviatePlayerNames,
  );
  const fullName = resolveGamePlayerDisplayName(name, canonicalPlayerName, false);
  return (
    <Box
      component="span"
      title={
        abbreviatePlayerNames && fullName !== displayName ? fullName : undefined
      }
    >
      {displayName}
    </Box>
  );
};

const WrappedGameRow = ({
  game,
  showSource,
  showAnalysisStatus,
  analyzingGameId,
  onAnalyzeGame,
  onCancelScheduledAnalysis,
  manageMode,
  onDeleteGame,
  deletingGameId,
  canonicalPlayerName,
  abbreviatePlayerNames,
}: {
  game: GameSummaryRow;
  showSource: boolean;
  showAnalysisStatus?: boolean;
  analyzingGameId?: string | null;
  onAnalyzeGame?: (gameId: string) => void;
  onCancelScheduledAnalysis?: (gameId: string) => void;
  manageMode?: boolean;
  onDeleteGame?: (gameId: string) => void;
  deletingGameId?: string | null;
  canonicalPlayerName?: string;
  abbreviatePlayerNames: boolean;
}) => {
  const timeLabel =
    formatTimeControlLabel(game.timeControl, game.timeClass) ?? '—';
  const dateLabel = formatUtcDateLabel(game.date) ?? '—';

  return (
    <>
      <Box sx={wrappedGameRowLine1Sx}>
        {renderPlayerName(game.white, canonicalPlayerName, abbreviatePlayerNames)}
        <Box component="span">{game.whiteElo}</Box>
        {renderPlayerName(game.black, canonicalPlayerName, abbreviatePlayerNames)}
        <Box component="span">{game.blackElo}</Box>
        <Box component="span">{game.result}</Box>
      </Box>
      <Box sx={wrappedGameRowLine2Sx}>
        <Box component="span">{timeLabel}</Box>
        <Box component="span">{dateLabel}</Box>
        <EcoOpeningLabel
          fields={game}
          sx={{
            maxWidth: 'none',
            overflow: 'visible',
            textOverflow: 'clip',
            whiteSpace: 'normal',
          }}
        />
        {showSource ? (
          <Box component="span">
            {formatGameSourceLabel(game.source)}
          </Box>
        ) : null}
        <GameAnalysisTrailingGroup
          game={game}
          showAnalysisStatus={showAnalysisStatus}
          analyzingGameId={analyzingGameId}
          onAnalyzeGame={onAnalyzeGame}
          onCancelScheduledAnalysis={onCancelScheduledAnalysis}
          manageMode={manageMode}
          onDeleteGame={onDeleteGame}
          deletingGameId={deletingGameId}
        />
      </Box>
    </>
  );
};

const CompactGameRow = ({
  game,
  showSource,
  showAnalysisStatus,
  analyzingGameId,
  onAnalyzeGame,
  onCancelScheduledAnalysis,
  manageMode,
  onDeleteGame,
  deletingGameId,
  canonicalPlayerName,
  abbreviatePlayerNames,
}: {
  game: GameSummaryRow;
  showSource: boolean;
  showAnalysisStatus?: boolean;
  analyzingGameId?: string | null;
  onAnalyzeGame?: (gameId: string) => void;
  onCancelScheduledAnalysis?: (gameId: string) => void;
  manageMode?: boolean;
  onDeleteGame?: (gameId: string) => void;
  deletingGameId?: string | null;
  canonicalPlayerName?: string;
  abbreviatePlayerNames: boolean;
}) => (
  <>
    {renderPlayerName(game.white, canonicalPlayerName, abbreviatePlayerNames)}
    <Box component="span">{game.whiteElo}</Box>
    {renderPlayerName(game.black, canonicalPlayerName, abbreviatePlayerNames)}
    <Box component="span">{game.blackElo}</Box>
    <Box component="span">{game.result}</Box>
    <Box
      component="span"
      sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
    >
      {formatTimeControlLabel(game.timeControl, game.timeClass) ?? '—'}
    </Box>
    <Box
      component="span"
      sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
    >
      {formatUtcDateLabel(game.date) ?? '—'}
    </Box>
    <EcoOpeningLabel fields={game} />
    {showSource ? (
      <Box
        component="span"
        sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
      >
        {formatGameSourceLabel(game.source)}
      </Box>
    ) : null}
    <GameAnalysisTrailingGroup
      game={game}
      showAnalysisStatus={showAnalysisStatus}
      analyzingGameId={analyzingGameId}
      onAnalyzeGame={onAnalyzeGame}
      onCancelScheduledAnalysis={onCancelScheduledAnalysis}
      manageMode={manageMode}
      onDeleteGame={onDeleteGame}
      deletingGameId={deletingGameId}
    />
  </>
);

const renderPreviewBoard = (
  game: ReplayGameSummary,
  showPreview: boolean,
) =>
  showPreview ? (
    <Box sx={gameSummaryPreviewBoardSx}>
      <ReplayGamePreviewBoard
        gameId={game.gameId}
        openingSans={game.openingSans}
        fillContainer
      />
    </Box>
  ) : null;

const wrapGameSummaryRow = (
  game: ReplayGameSummary,
  showInlineBoard: boolean,
  row: React.ReactElement,
) =>
  showInlineBoard ? (
    row
  ) : (
    <GameHoverPreview
      key={game.gameId}
      gameId={game.gameId}
      openingSans={game.openingSans}
    >
      {row}
    </GameHoverPreview>
  );

const GameSummaryTable = ({
  games,
  emptyMessage,
  onGameSelect,
  showSource = true,
  showAnalysisStatus = false,
  onAnalyzeGame,
  onCancelScheduledAnalysis,
  analyzingGameId = null,
  manageMode = false,
  onDeleteGame,
  deletingGameId = null,
  canonicalPlayerName,
  abbreviatePlayerNames = true,
  wrappedRows: wrappedRowsProp,
  showReplayAccess = false,
  showPreviewBoardOnFirstRow = false,
  tableSx,
}: GameSummaryTableProps) => {
  const wrappedRowsMobile = useWrappedGameRows();
  const wrappedRows = wrappedRowsProp ?? wrappedRowsMobile;
  const viewedGameIds = useGameViewedStatus(games);
  const isGameViewed = (gameId: string) => viewedGameIds.has(gameId);
  const isGameLocked = (game: ReplayGameSummary) =>
    showReplayAccess && game.replayAccessible === false;
  const cols = columnCount(showSource, showReplayAccess);
  const dataColSpan = columnCount(showSource, false) - 1;

  return (
    <TableContainer>
    <Table
      size="small"
      sx={
        tableSx ?? (wrappedRows ? wrappedGameTableSx : compactTableSx)
      }
    >
      <TableHead>
        {wrappedRows ? (
          <TableRow>
            <TableCell sx={seenColumnCellSx}>Seen</TableCell>
            <TableCell colSpan={dataColSpan}>
              <Box sx={wrappedGameRowLine1Sx}>
                <Box component="span">White</Box>
                <Box component="span">Elo</Box>
                <Box component="span">Black</Box>
                <Box component="span">Elo</Box>
                <Box component="span">Result</Box>
              </Box>
              <Box sx={{ ...wrappedGameRowLine2Sx, mt: 0 }}>
                <Box component="span">Time</Box>
                <Box component="span">Date</Box>
                <Box component="span">Opening</Box>
                {showSource ? <Box component="span">Source</Box> : null}
              </Box>
            </TableCell>
          </TableRow>
        ) : (
          <TableRow>
            <TableCell sx={seenColumnCellSx}>Seen</TableCell>
            <TableCell>White</TableCell>
            <TableCell>Elo</TableCell>
            <TableCell>Black</TableCell>
            <TableCell>Elo</TableCell>
            <TableCell>Result</TableCell>
            <TableCell>Time</TableCell>
            <TableCell>Date</TableCell>
            <TableCell>Opening</TableCell>
            {showSource ? <TableCell>Source</TableCell> : null}
            {showReplayAccess ? <TableCell>Access</TableCell> : null}
          </TableRow>
        )}
      </TableHead>
      <TableBody>
        {games.length === 0 ? (
          <TableRow>
            <TableCell
              colSpan={cols}
              sx={{ fontStyle: 'italic', color: 'text.secondary' }}
            >
              {emptyMessage}
            </TableCell>
          </TableRow>
        ) : wrappedRows ? (
          games.map((game, index) => {
            const showInlineBoard = showPreviewBoardOnFirstRow && index === 0;
            return wrapGameSummaryRow(
              game,
              showInlineBoard,
              <TableRow
                key={game.gameId}
                hover
                onClick={() => onGameSelect(game)}
                sx={
                  [
                    replayGameRowSx,
                    isGameLocked(game)
                      ? { opacity: 0.72, cursor: 'pointer' }
                      : null,
                  ] as SxProps<Theme>
                }
              >
                <TableCell sx={seenColumnCellSx}>
                  <GameViewedIcon viewed={isGameViewed(game.gameId)} />
                </TableCell>
                <TableCell colSpan={dataColSpan}>
                  <WrappedGameRow
                    game={game}
                    showSource={showSource}
                    showAnalysisStatus={showAnalysisStatus}
                    analyzingGameId={analyzingGameId}
                    onAnalyzeGame={onAnalyzeGame}
                    onCancelScheduledAnalysis={onCancelScheduledAnalysis}
                    manageMode={manageMode}
                    onDeleteGame={onDeleteGame}
                    deletingGameId={deletingGameId}
                    canonicalPlayerName={canonicalPlayerName}
                    abbreviatePlayerNames={abbreviatePlayerNames}
                  />
                  {renderPreviewBoard(game, showInlineBoard)}
                </TableCell>
                {showReplayAccess ? (
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {isGameLocked(game) ? (
                      <Chip
                        label="Upgrade"
                        size="small"
                        color="warning"
                        variant="outlined"
                      />
                    ) : null}
                  </TableCell>
                ) : null}
              </TableRow>,
            );
          })
        ) : (
          games.map((game, index) => {
            const showInlineBoard =
              showPreviewBoardOnFirstRow && index === 0;
            const rowSx = [
              replayGameRowSx,
              isGameLocked(game)
                ? { opacity: 0.72, cursor: 'pointer' }
                : null,
            ] as SxProps<Theme>;

            if (showInlineBoard) {
              return (
                <TableRow
                  key={game.gameId}
                  hover
                  onClick={() => onGameSelect(game)}
                  sx={rowSx}
                >
                  <TableCell sx={seenColumnCellSx}>
                    <GameViewedIcon viewed={isGameViewed(game.gameId)} />
                  </TableCell>
                  <TableCell colSpan={dataColSpan}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'baseline',
                        columnGap: 1,
                        rowGap: 0.25,
                      }}
                    >
                      <CompactGameRow
                        game={game}
                        showSource={showSource}
                        showAnalysisStatus={showAnalysisStatus}
                        analyzingGameId={analyzingGameId}
                        onAnalyzeGame={onAnalyzeGame}
                        onCancelScheduledAnalysis={onCancelScheduledAnalysis}
                        manageMode={manageMode}
                        onDeleteGame={onDeleteGame}
                        deletingGameId={deletingGameId}
                        canonicalPlayerName={canonicalPlayerName}
                        abbreviatePlayerNames={abbreviatePlayerNames}
                      />
                    </Box>
                    {renderPreviewBoard(game, true)}
                  </TableCell>
                  {showReplayAccess ? (
                    <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                      {isGameLocked(game) ? (
                        <Chip
                          label="Upgrade"
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                      ) : null}
                    </TableCell>
                  ) : null}
                </TableRow>
              );
            }

            return wrapGameSummaryRow(
              game,
              false,
              <TableRow
                key={game.gameId}
                hover
                onClick={() => onGameSelect(game)}
                sx={rowSx}
              >
                <TableCell sx={seenColumnCellSx}>
                  <GameViewedIcon viewed={isGameViewed(game.gameId)} />
                </TableCell>
                <TableCell>
                  {renderPlayerName(
                    game.white,
                    canonicalPlayerName,
                    abbreviatePlayerNames,
                  )}
                </TableCell>
                <TableCell>{game.whiteElo}</TableCell>
                <TableCell>
                  {renderPlayerName(
                    game.black,
                    canonicalPlayerName,
                    abbreviatePlayerNames,
                  )}
                </TableCell>
                <TableCell>{game.blackElo}</TableCell>
                <TableCell>{game.result}</TableCell>
                <TableCell
                  sx={{
                    color: 'text.secondary',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formatTimeControlLabel(game.timeControl, game.timeClass) ??
                    '—'}
                </TableCell>
                <TableCell
                  sx={{
                    color: 'text.secondary',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formatUtcDateLabel(game.date) ?? '—'}
                </TableCell>
                <TableCell>
                  <EcoOpeningLabel fields={game} />
                </TableCell>
                {showSource ? (
                  <TableCell
                    sx={{
                      color: 'text.secondary',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.75,
                        width: '100%',
                      }}
                    >
                      {formatGameSourceLabel(game.source)}
                      <GameAnalysisTrailingGroup
                        game={game}
                        showAnalysisStatus={showAnalysisStatus}
                        analyzingGameId={analyzingGameId}
                        onAnalyzeGame={onAnalyzeGame}
                        onCancelScheduledAnalysis={onCancelScheduledAnalysis}
                        manageMode={manageMode}
                        onDeleteGame={onDeleteGame}
                        deletingGameId={deletingGameId}
                      />
                    </Box>
                  </TableCell>
                ) : null}
                {showReplayAccess ? (
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {isGameLocked(game) ? (
                      <Chip
                        label="Upgrade"
                        size="small"
                        color="warning"
                        variant="outlined"
                      />
                    ) : null}
                  </TableCell>
                ) : null}
              </TableRow>,
            );
          })
        )}
      </TableBody>
    </Table>
    </TableContainer>
  );
};

export default GameSummaryTable;
